import { Component, effect, inject, OnDestroy, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { finalize, Subject } from 'rxjs';
import { DynamicFilterConfig, createDynamicFilterState } from '@card-software/library';
import { TranslateService } from '@ngx-translate/core';
import { PaginatorState } from 'primeng/paginator';
import { ApDocumentStatus } from '../../../models/ap-document/ap-document';
import {
  WithholdingDocument,
  WithholdingDocumentSearchCommand,
  WithholdingEntityKey,
} from '../../../models/withholding-document/withholding-document';
import { WithholdingDocumentProxyService } from '../../../proxies/withholding-document.proxy.service';
import { EnumService } from '../../../services/enum.service';

/**
 * Withholding Certificates (Constancia de Retención) list (#4800). Reads the withholding-document endpoint
 * directly. It used to run the AP document search hard-scoped to DocumentType.Retention, with a matching
 * rule in the AP repository to hide retentions from every OTHER query — both of which existed only because
 * retentions shared the AP collection. Rows open the detail; "New" starts from an invoice search.
 */
@Component({
  selector: 'app-ap-withholdings',
  templateUrl: './ap-withholdings.component.html',
  styleUrl: './ap-withholdings.component.scss',
})
export class ApWithholdingsComponent implements OnDestroy {
  private readonly router = inject(Router);
  private readonly service = inject(WithholdingDocumentProxyService);
  private readonly enumService = inject(EnumService);
  private readonly translateService = inject(TranslateService);

  private readonly destroy$ = new Subject<void>();
  private lastSearchCriteria: WithholdingDocumentSearchCommand = {};

  private statusLabels: Record<number, string> = {};
  private currencyLabels: Record<number, string> = {};

  isLoading = signal(0);
  enumsReady = signal(false);
  filterConfig = signal<DynamicFilterConfig | null>(null);
  showFilters = signal(true);
  readonly filterState = createDynamicFilterState({ storageKey: 'ap-withholdings-filters', config: this.filterConfig });
  retentions = signal<WithholdingDocument[]>([]);

  currentPage = signal(1);
  pageSize = signal(25);
  totalRecords = signal(0);

  constructor() {
    this.filterState.applied$
      .pipe(takeUntilDestroyed())
      .subscribe((filters) => this.searchWithFilters(filters as WithholdingDocumentSearchCommand));

    effect(
      () => {
        if (!this.enumService.enumsReady()) return;
        this.statusLabels = this.enumService.getEnum('ApDocumentStatus').values as Record<number, string>;
        this.currencyLabels = this.enumService.getEnum('Currency').values as Record<number, string>;
        this.enumsReady.set(true);
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        if (this.translateService.currentLang && this.enumsReady()) {
          this.tryInitializeFilterConfig();
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private startLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((v) => v - 1);
  }

  private translate(key: string, fallback: string): string {
    const translation = this.translateService.instant(key);
    return translation === key ? fallback : translation;
  }

  private tryInitializeFilterConfig(): void {
    this.filterConfig.set(this.buildFilterConfig());
  }

  private buildFilterConfig(): DynamicFilterConfig {
    return {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'number',
          label: this.translate('ApDocument_Number', 'Certificate #'),
          placeholder: this.translate('ApDocument_Number_Placeholder', 'Enter certificate number'),
          defaultVisible: true,
        },
        {
          type: 'select',
          formControlName: 'status',
          label: this.translate('Status', 'Status'),
          placeholder: this.translate('Select_status', 'Select status'),
          options: this.statusLabels,
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'date',
          formControlName: 'documentDateFrom',
          label: this.translate('ApDocument_DocumentDateFrom', 'Date from'),
          defaultVisible: false,
        },
        {
          type: 'date',
          formControlName: 'documentDateTo',
          label: this.translate('ApDocument_DocumentDateTo', 'Date to'),
          defaultVisible: false,
        },
      ],
    };
  }

  private search(criteria: WithholdingDocumentSearchCommand): void {
    // No document-type scoping needed any more: the collection holds nothing else.
    this.lastSearchCriteria = criteria;
    this.startLoading();
    this.service
      .search(criteria, this.currentPage(), this.pageSize())
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          this.retentions.set(response?.data?.items ?? []);
          this.totalRecords.set(response?.data?.totalCount ?? 0);
        },
        error: () => {
          this.retentions.set([]);
          this.totalRecords.set(0);
        },
      });
  }

  private searchWithFilters(filters: WithholdingDocumentSearchCommand): void {
    const criteria: WithholdingDocumentSearchCommand = {
      ...filters,
      status: filters.status != null ? Number(filters.status) : null,
      documentDateFrom: this.toIso(filters.documentDateFrom),
      documentDateTo: this.toIso(filters.documentDateTo),
    };
    this.currentPage.set(1);
    this.search(criteria);
  }

  private toIso(value: unknown): string | null {
    if (!value) return null;
    return value instanceof Date ? value.toISOString() : String(value);
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage.set((event.page ?? 0) + 1);
    this.pageSize.set(event.rows ?? this.pageSize());
    this.search(this.lastSearchCriteria);
  }

  /** Start a new certificate — the detail page opens on its invoice search. */
  createNew(): void {
    this.router.navigate(['/ap-withholding-details']);
  }

  openDetails(id?: string): void {
    if (!id) return;
    this.router.navigate(['/ap-withholding-details'], { queryParams: { id } });
  }

  statusLabel(status: number): string {
    return this.statusLabels[status] ?? '';
  }

  currencyLabel(currency: number | null | undefined): string {
    if (currency === null || currency === undefined) return '';
    return this.currencyLabels[currency] ?? '';
  }

  statusSeverity(status: number): 'success' | 'warn' | 'info' | 'danger' | 'secondary' {
    switch (status) {
      case ApDocumentStatus.Posted:
        return 'success';
      case ApDocumentStatus.Draft:
        return 'secondary';
      case ApDocumentStatus.Cancelled:
        return 'danger';
      default:
        return 'info';
    }
  }

  providerDisplay(document: WithholdingDocument): string {
    return document.providerName || document.oneTimeVendorName || document.providerCode || '';
  }

  getRowData(rowData: unknown): WithholdingDocument {
    return rowData as WithholdingDocument;
  }

  /**
   * The AP document a certificate's row names (#5122). A certificate for a foreign provider can cite
   * several, so the column shows the first and counts the rest rather than silently naming one of many.
   */
  firstSource(document: WithholdingDocument): WithholdingEntityKey | null {
    return document.sources?.[0]?.document ?? document.invoiceReference ?? null;
  }

  /** How many documents beyond the one shown. 0 renders nothing. */
  extraSourceCount(document: WithholdingDocument): number {
    return Math.max((document.sources?.length ?? 0) - 1, 0);
  }
}
